
import { Package as PackageIcon, CalendarCheck, TrendingUp, Star, CircleSlash } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  money, pct, th, td, useCanSeeMoney, useRange, useReport, yearRange, type ExportColumn, type ExportSpec,
} from "../ReportKit";

interface PackageRow {
  id: number; name: string; is_active: boolean;
  bookings: number; drafts: number; cancelled: number; guests: number; last_booked: string | null;
  offered_price?: number; revenue?: number; profit?: number; avg_revenue?: number; margin?: number;
}
interface PackageReport {
  can_view_money: boolean;
  packages: PackageRow[];
  summary: {
    total_packages: number; packages_used: number; bookings: number;
    events_without_package: number; drafts_excluded: number; cancelled_excluded: number;
    most_popular: { id: number; name: string; bookings: number } | null;
    revenue?: number; profit?: number;
    most_profitable?: { id: number; name: string; profit: number } | null;
  };
}

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#3b82f6", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899"];

export default function PackageAnalyticsPage() {
  const canSeeMoney = useCanSeeMoney();
  const range = useRange(yearRange(0));
  const { from, to } = range.applied;

  const state = useReport<PackageReport>(
    async () => (await reportsApi.packageAnalytics({ from_date: from, to_date: to })).data,
    [from, to],
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={PackageIcon}
          iconClass="text-indigo-600"
          title="Package Analytics"
          subtitle="Bookings per package for events starting in the period"
        >
          <HeaderTools>
            <RangeControls range={range} />
            {state.status !== 403 && (
              <ExportButtons
                build={() => state.data && buildExport(state.data, canSeeMoney && state.data.can_view_money, { from, to })}
                disabled={state.loading || !state.data}
              />
            )}
          </HeaderTools>
        </ReportHeader>

        <ReportBody state={state}>
          {(d) => <PackagesView d={d} showMoney={canSeeMoney && d.can_view_money} />}
        </ReportBody>
      </div>
    </DashboardLayout>
  );
}

type SummaryItem = NonNullable<ExportSpec["summary"]>[number];

/** The explanation under the report, with this period's draft / cancelled counts. */
const packagesNote = (s: PackageReport["summary"], showMoney: boolean) =>
  `Bookings = confirmed, in-progress and completed events. Drafts (${s.drafts_excluded}) and cancelled events (${s.cancelled_excluded}) in the period are not counted.` +
  (showMoney ? " Revenue is after discount and before GST; profit is revenue minus the event’s costs and expenses." : "");

/** Excel / Print: the headline figures and the package breakdown (money columns only for owner and admins). */
function buildExport(d: PackageReport, showMoney: boolean, period: { from: string; to: string }): ExportSpec {
  const s = d.summary;
  const guests = d.packages.reduce((t, p) => t + p.guests, 0);
  const revenue = s.revenue ?? 0;
  const profit = s.profit ?? 0;

  const columns: ExportColumn[] = [
    { label: "Package" },
    { label: "Status" },
    { label: "Bookings", type: "number" },
    { label: "Drafts (not counted)", type: "number" },
    { label: "Cancelled (not counted)", type: "number" },
    ...(showMoney ? [
      { label: "Revenue (before GST)", type: "money" },
      { label: "Profit", type: "money" },
      { label: "Margin", type: "percent" },
      { label: "Avg per booking", type: "money" },
    ] satisfies ExportColumn[] : []),
    { label: "Guests", type: "number" },
    { label: "Last booked", type: "date" },
  ];

  return {
    title: "Package Analytics",
    fileName: "package-analytics",
    period,
    summary: [
      { label: "Package bookings", value: s.bookings, type: "number" },
      { label: "Packages used", value: s.packages_used, type: "number" },
      { label: "Packages in total", value: s.total_packages, type: "number" },
      ...(showMoney ? [
        { label: "Revenue (before GST)", value: revenue, type: "money" },
        { label: "Profit", value: profit, type: "money" },
        ...(s.most_profitable ? [{ label: "Most profitable", value: s.most_profitable.name }] : []),
      ] satisfies SummaryItem[] : [
        ...(s.most_popular ? [{ label: "Most popular", value: `${s.most_popular.name} (${s.most_popular.bookings} bookings)` }] : []),
        { label: "Guests", value: guests, type: "number" },
      ] satisfies SummaryItem[]),
      { label: "Booked events without a package", value: s.events_without_package, type: "number" },
    ],
    tables: [
      {
        title: "Package breakdown",
        columns,
        rows: d.packages.map((p) => [
          p.name, p.is_active ? "Active" : "Archived", p.bookings, p.drafts, p.cancelled,
          ...(showMoney ? [
            p.revenue ?? 0, p.profit ?? 0,
            p.bookings ? p.margin ?? 0 : null,
            p.bookings ? p.avg_revenue ?? 0 : null,
          ] : []),
          p.guests, p.last_booked,
        ]),
        totals: d.packages.length ? [
          "Total", "", s.bookings,
          d.packages.reduce((t, p) => t + p.drafts, 0),
          d.packages.reduce((t, p) => t + p.cancelled, 0),
          ...(showMoney ? [revenue, profit, revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : null, null] : []),
          guests, null,
        ] : undefined,
      },
    ],
    notes: [packagesNote(s, showMoney)],
  };
}

function PackagesView({ d, showMoney }: { d: PackageReport; showMoney: boolean }) {
  const s = d.summary;
  const used = d.packages.filter((p) => p.bookings > 0);
  const chart = used.map((p, i) => ({
    name: p.name.length > 18 ? p.name.slice(0, 17) + "…" : p.name,
    value: showMoney ? p.profit ?? 0 : p.bookings,
    fill: COLORS[i % COLORS.length],
  }));

  return (
    <>
      <KpiGrid>
        <Kpi label="Package bookings" icon={CalendarCheck} tone="indigo" value={s.bookings} sub={`${s.packages_used} of ${s.total_packages} packages used`} />
        {showMoney ? (
          <>
            <Kpi label="Revenue (before GST)" icon={TrendingUp} tone="emerald" value={money(s.revenue)} sub="from package bookings" />
            <Kpi label="Profit" icon={Star} tone={(s.profit ?? 0) >= 0 ? "blue" : "red"} value={money(s.profit)}
              sub={s.most_profitable ? `Most profitable: ${s.most_profitable.name}` : "—"} />
          </>
        ) : (
          <>
            <Kpi label="Most popular" icon={Star} tone="emerald" value={s.most_popular?.name ?? "—"} sub={s.most_popular ? `${s.most_popular.bookings} bookings` : undefined} />
            <Kpi label="Guests" icon={TrendingUp} tone="blue" value={d.packages.reduce((t, p) => t + p.guests, 0).toLocaleString("en-IN")} sub="across package bookings" />
          </>
        )}
        <Kpi label="Without a package" icon={CircleSlash} tone="amber" value={s.events_without_package} sub="booked events" />
      </KpiGrid>

      {chart.length > 0 && (
        <Card title={showMoney ? "Profit per package" : "Bookings per package"}>
          <ResponsiveContainer width="100%" height={Math.max(160, chart.length * 40)}>
            <BarChart data={chart} layout="vertical" barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} tickFormatter={(v) => (showMoney ? money(v) : String(v))} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
              <Tooltip formatter={(v: any) => [showMoney ? money(v) : v, showMoney ? "Profit" : "Bookings"]} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {chart.map((c, i) => <Cell key={i} fill={c.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card title="Package breakdown">
        {d.packages.length === 0 ? (
          <p className="text-sm text-stone-400 py-8 text-center">No packages</p>
        ) : (
          <TableScroll minWidth={showMoney ? 820 : 520}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className={`${th} text-left`}>Package</th>
                  <th className={`${th} text-right`}>Bookings</th>
                  {showMoney && <th className={`${th} text-right`}>Revenue (before GST)</th>}
                  {showMoney && <th className={`${th} text-right`}>Profit</th>}
                  {showMoney && <th className={`${th} text-right`}>Margin</th>}
                  {showMoney && <th className={`${th} text-right`}>Avg per booking</th>}
                  <th className={`${th} text-right`}>Guests</th>
                  <th className={`${th} text-left`}>Last booked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {d.packages.map((p, i) => (
                  <tr key={p.id} className="hover:bg-stone-50">
                    <td className={td}>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="font-medium text-stone-800">{p.name}</span>
                        {!p.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">archived</span>}
                      </div>
                      {(p.drafts > 0 || p.cancelled > 0) && (
                        <div className="text-[11px] text-stone-400 ml-4">
                          {[p.drafts ? `${p.drafts} draft` : "", p.cancelled ? `${p.cancelled} cancelled` : ""].filter(Boolean).join(" · ")} not counted
                        </div>
                      )}
                    </td>
                    <td className={`${td} text-right text-stone-700`}>{p.bookings}</td>
                    {showMoney && <td className={`${td} text-right tabular-nums text-stone-800`}>{money(p.revenue)}</td>}
                    {showMoney && <td className={`${td} text-right tabular-nums font-semibold ${(p.profit ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{money(p.profit)}</td>}
                    {showMoney && <td className={`${td} text-right text-stone-600`}>{p.bookings ? pct(p.margin) : "—"}</td>}
                    {showMoney && <td className={`${td} text-right tabular-nums text-stone-500`}>{p.bookings ? money(p.avg_revenue) : "—"}</td>}
                    <td className={`${td} text-right text-stone-600`}>{p.guests.toLocaleString("en-IN")}</td>
                    <td className={`${td} text-xs text-stone-500`}>{p.last_booked ? formatDate(p.last_booked) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                  <td className={`${td} text-stone-800`}>Total</td>
                  <td className={`${td} text-right`}>{s.bookings}</td>
                  {showMoney && <td className={`${td} text-right tabular-nums`}>{money(s.revenue)}</td>}
                  {showMoney && <td className={`${td} text-right tabular-nums`}>{money(s.profit)}</td>}
                  {showMoney && <td className={`${td} text-right`}>{(s.revenue ?? 0) > 0 ? pct(((s.profit ?? 0) / (s.revenue ?? 1)) * 100) : "—"}</td>}
                  {showMoney && <td className={td} />}
                  <td className={`${td} text-right`}>{d.packages.reduce((t, p) => t + p.guests, 0).toLocaleString("en-IN")}</td>
                  <td className={td} />
                </tr>
              </tfoot>
            </table>
          </TableScroll>
        )}
      </Card>

      <Note>{packagesNote(s, showMoney)}</Note>
    </>
  );
}
